import React, { useEffect, useState } from "react";
import { useTranslation } from "../i18n";

import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearAuthSessionAction } from "../actions/auth";
import { getChatUnreadCountAction } from "../actions/chat";
import { getVendorProfileView } from "../utils/vendorProfile";

const SIDEBAR_ROUTE_FAMILIES = {
  "/dashboard": ["/dashboard", "/complete-profile"],
  "/inquiries": [
    "/inquiries",
    "/inquiry",
    "/add-inquiry",
    "/create-inquiry",
    "/edit-inquiry",
  ],
  "/inquiry": [
    "/inquiries",
    "/inquiry",
    "/add-inquiry",
    "/create-inquiry",
    "/edit-inquiry",
  ],
  "/order": ["/order", "/orders"],
  "/orders": ["/order", "/orders"],
  "/quimera-orders": ["/quimera-orders"],
  "/product": [
    "/product",
    "/products",
    "/add-product",
    "/create-product",
    "/edit-product",
  ],
  "/products": [
    "/product",
    "/products",
    "/add-product",
    "/create-product",
    "/edit-product",
  ],
  "/message": ["/message", "/messages"],
  "/messages": ["/message", "/messages"],
  "/vendor": ["/vendor", "/vendors"],
  "/vendors": ["/vendor", "/vendors"],
  "/client": ["/client", "/clients"],
  "/clients": ["/client", "/clients"],
  "/notification": ["/notification", "/notifications"],
  "/notifications": ["/notification", "/notifications"],
  "/setting": ["/setting", "/settings"],
  "/settings": ["/setting", "/settings"],
  "/transactions": ["/transactions", "/transaction"],
  "/transaction": ["/transactions", "/transaction"],
  "/document": ["/document", "/documents"],
  "/documents": ["/document", "/documents"],
  "/shipment": ["/shipment", "/shipments"],
  "/shipments": ["/shipment", "/shipments"],
  "/user": ["/user", "/users"],
  "/users": ["/user", "/users"],
  "/reports-analytics": ["/reports-analytics", "/reports", "/analytics"],
  "/reports": ["/reports", "/reports-analytics", "/analytics"],
  "/analytics": ["/analytics", "/reports-analytics", "/reports"],
};

const matchesPathFamily = (pathname, basePath) =>
  pathname === basePath || pathname.startsWith(`${basePath}/`);

const isSidebarItemActive = (itemPath, pathname) =>
  (SIDEBAR_ROUTE_FAMILIES[itemPath] || [itemPath]).some((basePath) =>
    matchesPathFamily(pathname, basePath),
  );

export default function Sidebar({ sidebarOpen, closeSidebar }) {
  const { t, text } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authMe = useSelector((state) => state.auth?.authMe || {});
  const unreadMessageCount = useSelector(
    (state) => Number(state.chat?.unreadCount) || 0,
  );
  const vendorProfileView = getVendorProfileView(authMe);
  const [collapsed, setCollapsed] = useState(false);
  const [operationsOpen, setOperationsOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const location = useLocation();
  const [logoutPopup, setLogoutPopup] = useState(false);

  useEffect(() => {
    const refreshUnreadCount = () => {
      if (document.visibilityState !== "hidden") {
        dispatch(getChatUnreadCountAction());
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshUnreadCount();
    };

    refreshUnreadCount();
    const intervalId = window.setInterval(refreshUnreadCount, 15000);
    window.addEventListener("focus", refreshUnreadCount);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshUnreadCount);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch, location.pathname]);
  const menuItems = [
    // {
    //   name: "Dashboard",
    //   path: "/dashboard",
    //   icon: "/assets/overview.svg",
    // },
    {
      name: "Received inquiries",
      path: "/inquiries",
      icon: "/assets/product.svg",
    },
    {
      name: "Order list",
      path: "/order",
      icon: "/assets/operationsidebar-icon.svg",
    },
    {
      name: "Quimera 360 orders",
      path: "/quimera-orders",
      icon: "/assets/shipment-icon.svg",
    },
    {
      name: "Product",
      path: "/product",
      icon: "/assets/product.svg",
    },
    {
      name: "Messages",
      path: "/message",
      icon: "/assets/message.svg",
    },
    // {
    //   name: "Documents",
    //   path: "/document",
    //   icon: "/assets/Project.svg",
    // },
    // {
    //   name: "Shipments",
    //   path: "/shipment",
    //   icon: "/assets/Shipments.svg",
    // },
    // {
    //   name: "Transactions",
    //   path: "/transactions",
    //   icon: "/assets/time.svg",
    // },
    // {
    //   name: "Users",
    //   path: "/user",
    //   icon: "/assets/users.svg",
    // },
    // {
    //   name: "Vendor",
    //   path: "/vendor",
    //   icon: "/assets/users.svg",
    // },

    // {
    //   name: "Reports & Analytics",
    //   path: "/reports-analytics",
    //   icon: "/assets/reports-analytics.svg",
    // },
    // {
    //   name: "Settings",
    //   path: "/setting",
    //   icon: "/assets/setting.svg",
    // },
  ];

  const isOperationsActive = ["/inquiries", "/order"].some((basePath) =>
    matchesPathFamily(location.pathname, basePath),
  );

  // Inquiries page open ho to dropdown open rahe
  useEffect(() => {
    if (isOperationsActive && !collapsed) {
      setOperationsOpen(true);
    }
  }, [isOperationsActive, collapsed]);

  const handleOperationsToggle = () => {
    // collapsed sidebar me click karne par sidebar expand karo
    if (collapsed) {
      setCollapsed(false);
      setOperationsOpen(true);
      return;
    }

    setOperationsOpen((prev) => !prev);
  };

  const handleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;

      if (next) {
        setOperationsOpen(false);
      }

      return next;
    });
  };

  // Logout dropdown button click
  const handleLogoutClick = () => {
    setMenuVisible(false);
    setLogoutPopup(true);
  };

  // Cancel logout
  const handleLogoutCancel = () => {
    setLogoutPopup(false);
  };

  // Confirm logout
  const handleLogoutConfirm = () => {
    dispatch(clearAuthSessionAction());

    setLogoutPopup(false);

    navigate("/", {
      replace: true,
    });
  };

  return (
    <>
      <aside
        className={`sidebar ${
          sidebarOpen ? "sidebar-open" : ""
        } ${collapsed ? "sidebar-collapsed" : ""}`}
      >
        {/* Logo */}
        <div className="sidebar-logo">
          <img
            src={collapsed ? "/assets/logo1.svg" : "/assets/Lualilogo.svg"}
            alt={t("ui.luali.commodities")}
          />
        </div>

        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={handleCollapse}
          aria-label={collapsed ? t("ui.expandSidebar") : t("ui.collapseSidebar")}
        >
          <span className={collapsed ? "collapsed-arrow" : ""}>‹</span>
        </button>

        <div className="sidebar-content">
          <nav className="sidebar-nav">
            <NavLink
              to="/dashboard"
              onClick={closeSidebar}
              title={collapsed ? t("ui.dashboard") : undefined}
              className={() =>
                `sidebar-menu-item ${
                  isSidebarItemActive("/dashboard", location.pathname)
                    ? "active"
                    : ""
                }`
              }
            >
              <span className="sidebar-icon-wrapper">
                <img
                  src="/assets/overview.svg"
                  alt=""
                  className="sidebar-menu-icon"
                />
              </span>
              <span className="sidebar-menu-text">{t("ui.dashboard")}</span>
            </NavLink>

            {/* Operations Dropdown */}
            {/* <div
              className={`sidebar-operations-dropdown ${
                operationsOpen ? "dropdown-open" : ""
              }`}
            >
              <button
                type="button"
                className={`sidebar-menu-item sidebar-operations-btn ${
                  isOperationsActive ? "active" : ""
                }`}
                onClick={handleOperationsToggle}
                aria-expanded={operationsOpen}
                title={collapsed ? "Operations" : undefined}
              >
                <span className="sidebar-icon-wrapper">
                  <img
                    src="/assets/operationsidebar-icon.svg"
                    alt=""
                    className="sidebar-menu-icon"
                  />
                </span>
                <span className="sidebar-menu-text">Operations</span>
                {!collapsed && (
                  <svg
                    className={`sidebar-operations-chevron ${
                      operationsOpen ? "open" : ""
                    }`}
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path d="M6.5 8L10 11.5L13.5 8" />
                  </svg>
                )}
              </button>
              {!collapsed && operationsOpen && (
                <div className="sidebar-operations-submenu">
                  <NavLink
                    to="/inquiries"
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `sidebar-operations-submenu-item ${
                        isActive ? "active" : ""
                      }`
                    }
                  >
                    <span className="sidebar-submenu-icon">
                      <img src="/assets/Resource.svg" alt="" />
                    </span>

                    <span>Inquiries</span>
                  </NavLink>
                  <NavLink
                    to="/order"
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `sidebar-operations-submenu-item ${
                        isActive ? "active" : ""
                      }`
                    }
                  >
                    <span className="sidebar-submenu-icon">
                      <img src="/assets/Resource.svg" alt="" />
                    </span>

                    <span>Active operations</span>
                  </NavLink>
                </div>
              )}
            </div> */}

            {/* Remaining menu */}
            {menuItems
              .filter((item) => item.name !== "Overview")
              .map((item) => (
                <NavLink
                  key={item.name}
                  to={item.path}
                  onClick={closeSidebar}
                  title={collapsed ? text(item.name) : undefined}
                  className={() =>
                    `sidebar-menu-item ${
                      isSidebarItemActive(item.path, location.pathname)
                        ? "active"
                        : ""
                    }`
                  }
                >
                  <span className="sidebar-icon-wrapper">
                    <img src={item.icon} alt="" className="sidebar-menu-icon" />
                    {item.path === "/message" && unreadMessageCount > 0 && (
                      <span
                        className="sidebar-unread-badge"
                        role="status"
                        aria-label={"Unread messages: " + unreadMessageCount}
                      >
                        {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                      </span>
                    )}
                  </span>

                  <span className="sidebar-menu-text">{text(item.name)}</span>
                </NavLink>
              ))}
          </nav>
        </div>

        {/* Bottom User Profile */}
        <div className="sidebar-bottom-profile">
          <div className="sidebar-profile-line" />

          <div className="sidebar-profile">
            <div className="sidebar-profile-avatar">
              <span>{vendorProfileView.initial}</span>
              {vendorProfileView.imageUrl && (
                <img
                  src={vendorProfileView.imageUrl}
                  alt={vendorProfileView.name ? `${vendorProfileView.name} ${t("ui.profile")}` : t("ui.profile")}
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              )}
            </div>

            {!collapsed && (
              <>
                <div className="sidebar-profile-info">
                  <p>{vendorProfileView.name || t("ui.vendor")}</p>
                  <span>{vendorProfileView.email || ""}</span>
                </div>

                <button
                  type="button"
                  className="sidebar-profile-logout"
                  aria-label={t("ui.logout")}
                  onClick={handleLogoutClick}
                >
                  <img src="/assets/logout.svg" alt="" />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <button
          type="button"
          className="sidebar-overlay"
          onClick={closeSidebar}
          aria-label={t("ui.close.sidebar")}
        />
      )}

      {/* LOGOUT CONFIRMATION POPUP */}
      {logoutPopup && (
        <div
          className="logout-modal-overlay"
          onClick={handleLogoutCancel}
          role="presentation"
        >
          <div
            className="logout-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-title"
          >
            <div className="logout-modal-icon">
              <img src="/assets/logout.svg" alt="" />
            </div>

            <h3 id="logout-title">{t("ui.logout.x70xcl")}</h3>

            <p>{t("ui.are.you.sure.you.want.to.logout.from.your.account")}</p>

            <div className="logout-modal-actions">
              <button
                type="button"
                className="logout-cancel-btn"
                onClick={handleLogoutCancel}
              >{t("ui.cancel")}</button>

              <button
                type="button"
                className="logout-confirm-btn"
                onClick={handleLogoutConfirm}
              >{t("ui.logout.a799ze")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
